import axios from 'axios';

// API Configuration - Backend on api.shunrindo.com subdomain
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.shunrindo.com/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add tenant header and auth token
api.interceptors.request.use((config) => {
  // Add tenant ID header (default to '1' for public booking)
  const tenantId = localStorage.getItem('tenantId') || '1';
  config.headers['X-Tenant-ID'] = tenantId;

  // Add JWT token if exists
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  return config;
});

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors (token expired) - attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        // Try to refresh the access token
        const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken
        });

        // Update stored token
        localStorage.setItem('accessToken', data.accessToken);

        // Retry the original request with new token
        originalRequest.headers['Authorization'] = `Bearer ${data.accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, logout user
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('tenantId');

        // Dispatch custom event to notify AuthContext to clear user state
        // This triggers ProtectedRoute to redirect to /login
        window.dispatchEvent(new Event('auth:logout'));

        // Don't use window.location.href as it causes reload loops
        // AuthContext will clear user state, ProtectedRoute will redirect
        return Promise.reject(new Error('Session expired. Please log in again.'));
      }
    }

    if (error.response) {
      console.error('API Error:', error.response.data);
    }
    return Promise.reject(error);
  }
);

// Services API
export const servicesAPI = {
  getAll: () => api.get('/services'),
  getById: (id) => api.get(`/services/${id}`),
};

// Staff API
export const staffAPI = {
  getAll: () => api.get('/staff'),
  getById: (id) => api.get(`/staff/${id}`),
  getAvailability: (staffId, date) =>
    api.get(`/staff/${staffId}/availability`, { params: { date } }),
};

// Bookings API
export const bookingsAPI = {
  create: (bookingData) => api.post('/bookings', bookingData),
  getById: (id) => api.get(`/bookings/${id}`),
  update: (id, data) => api.patch(`/bookings/${id}`, data),
  cancel: (id) => api.delete(`/bookings/${id}`),
  getAvailableSlots: (date, serviceId, staffId) =>
    api.get('/bookings/available-slots', {
      params: { date, serviceId, staffId },
    }),
};

// Clients API
export const clientsAPI = {
  create: (clientData) => api.post('/clients', clientData),
  getByEmail: (email) => api.get('/clients', { params: { email } }),
};

// Authentication API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  refreshToken: (refreshToken) => api.post('/auth/refresh', { refreshToken })
};

// Admin API
export const adminAPI = {
  // Staff Management
  staff: {
    getAll: () => api.get('/admin/staff'),
    getById: (id) => api.get(`/admin/staff/${id}`),
    create: (data) => api.post('/admin/staff', data),
    update: (id, data) => api.put(`/admin/staff/${id}`, data),
    delete: (id) => api.delete(`/admin/staff/${id}`),
    toggle: (id) => api.patch(`/admin/staff/${id}/toggle`),
    updateSchedule: (id, schedule) => api.post(`/admin/staff/${id}/schedule`, { schedule })
  },

  // Services Management
  services: {
    getAll: () => api.get('/admin/services'),
    getById: (id) => api.get(`/admin/services/${id}`),
    create: (data) => api.post('/admin/services', data),
    update: (id, data) => api.put(`/admin/services/${id}`, data),
    delete: (id) => api.delete(`/admin/services/${id}`),
    toggle: (id) => api.patch(`/admin/services/${id}/toggle`)
  },

  // Service Categories
  categories: {
    getAll: () => api.get('/admin/services/categories'),
    create: (data) => api.post('/admin/services/categories', data),
    update: (id, data) => api.put(`/admin/services/categories/${id}`, data),
    delete: (id) => api.delete(`/admin/services/categories/${id}`)
  },

  // Bookings Management (placeholder for future implementation)
  bookings: {
    getAll: (params) => api.get('/admin/bookings', { params }),
    getById: (id) => api.get(`/admin/bookings/${id}`),
    create: (data) => api.post('/admin/bookings', data),
    update: (id, data) => api.put(`/admin/bookings/${id}`, data),
    updateStatus: (id, status) => api.put(`/admin/bookings/${id}/status`, { status })
  },

  // Clients Directory (placeholder for future implementation)
  clients: {
    getAll: (params) => api.get('/admin/clients', { params }),
    getById: (id) => api.get(`/admin/clients/${id}`),
    update: (id, data) => api.put(`/admin/clients/${id}`, data),
    getBookings: (id) => api.get(`/admin/clients/${id}/bookings`)
  },

  // Settings (placeholder for future implementation)
  settings: {
    get: () => api.get('/admin/settings'),
    update: (data) => api.put('/admin/settings', data)
  },

  // Dashboard Analytics (placeholder for future implementation)
  dashboard: {
    getSummary: () => api.get('/admin/dashboard/summary')
  },

  // Locations Management
  locations: {
    getAll: () => api.get('/admin/locations'),
    getById: (id) => api.get(`/admin/locations/${id}`),
    create: (data) => api.post('/admin/locations', data),
    update: (id, data) => api.put(`/admin/locations/${id}`, data),
    updateHours: (id, hours) => api.put(`/admin/locations/${id}/hours`, { hours }),
    delete: (id) => api.delete(`/admin/locations/${id}`)
  }
};

export default api;
